import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { QRCodeSVG } from "qrcode.react";
import { ShieldCheck, Copy, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";

type Status = { required: boolean; mode: "setup" | "challenge" | "email-otp"; verified: boolean };
type Setup = { secret: string; otpauthUri: string };

async function readJsonResponse(response: Response): Promise<Record<string, any>> {
  const contentType = response.headers.get("content-type") ?? "";
  const body = await response.text();
  if (!body) return {};
  if (!contentType.toLowerCase().includes("application/json")) {
    throw new Error(
      response.ok
        ? "The server returned an unexpected response. Please try again."
        : "The server could not verify the code right now. Please try again.",
    );
  }
  try {
    return JSON.parse(body) as Record<string, any>;
  } catch {
    throw new Error("The server returned an invalid response. Please try again.");
  }
}

export function twoFactorRoute(status?: Partial<Status> | null) {
  return status?.required && !status.verified ? "/two-factor" : null;
}

export default function TwoFactorPage() {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<Status | null>(null);
  const [setup, setSetup] = useState<Setup | null>(null);
  const [code, setCode] = useState("");
  const [recovery, setRecovery] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [resent, setResent] = useState(false);
  const [trustDevice, setTrustDevice] = useState(true);
  const [trustSaveFailed, setTrustSaveFailed] = useState(false);

  useEffect(() => {
    fetch("/api/auth/2fa/status", { credentials: "include" }).then(async response => {
      if (!response.ok) throw new Error("Unable to check verification status.");
      return response.json();
    }).then(setStatus).catch(err => setError(err.message));
  }, []);

  // Auto-fetch TOTP setup secret when in legacy setup mode
  useEffect(() => {
    if (status?.mode !== "setup" || setup) return;
    setBusy(true);
    fetch("/api/auth/2fa/setup", { method: "POST", credentials: "include" }).then(async response => {
      if (!response.ok) throw new Error((await response.json()).error || "Unable to start setup.");
      return response.json();
    }).then(setSetup).catch(err => setError(err.message)).finally(() => setBusy(false));
  }, [status?.mode, setup]);

  /** Safe same-origin returnTo destination; assistants land on Tasks by default. */
  const getReturnTo = (): string => {
    const val = new URLSearchParams(window.location.search).get("returnTo");
    return val && /^\/[^/]/.test(val) ? val : "/tasks";
  };

  // ── Email OTP handlers ────────────────────────────────────────────────────
  const submitEmailOtp = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!/^\d{6}$/.test(code)) return setError("Enter the 6-digit code from your email.");
    setBusy(true); setError(""); setResent(false);
    try {
      const response = await fetch("/api/auth/email-otp/verify", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ code, trustDevice }),
      });
      const result = await readJsonResponse(response);
      if (!response.ok || !result.ok) throw new Error(result.error || "That code could not be verified.");
      // The OTP has been consumed and the session is verified even if the
      // optional device-trust write failed. Do not prompt for the code again.
      if (trustDevice && result.deviceTrustStatus !== "saved") {
        setTrustSaveFailed(true);
        return;
      }
      await queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
      window.location.assign(getReturnTo());
    } catch (err: any) { setError(err.message || "Verification failed."); }
    finally { setBusy(false); }
  };

  const resendEmailOtp = async () => {
    setBusy(true); setError(""); setResent(false);
    try {
      const response = await fetch("/api/auth/email-otp/resend", {
        method: "POST",
        credentials: "include",
      });
      const result = await readJsonResponse(response);
      if (!response.ok) throw new Error(result.error || "Failed to resend.");
      setResent(true);
      setCode("");
    } catch (err: any) { setError(err.message || "Failed to resend code."); }
    finally { setBusy(false); }
  };

  // ── Legacy TOTP challenge/setup handler ────────────────────────────────────
  const submitTotp = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!recovery && !/^\d{6}$/.test(code)) return setError("Enter the 6-digit code from your authenticator.");
    if (recovery && !code.trim()) return setError("Enter a recovery code.");
    setBusy(true); setError("");
    try {
      const endpoint = status?.mode === "setup"
        ? "/api/auth/2fa/confirm"
        : recovery ? "/api/auth/2fa/recovery" : "/api/auth/2fa/verify";
      const body = status?.mode === "setup" ? { secret: setup?.secret, code } : { code };
      const response = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body),
      });
      const result = await readJsonResponse(response);
      if (!response.ok || !result.ok) throw new Error(result.error || "That code could not be verified.");
      if (status?.mode === "setup") {
        setRecoveryCodes(result.recoveryCodes || []);
      } else {
        await queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
        window.location.assign(getReturnTo());
      }
    } catch (err: any) { setError(err.message || "Verification failed."); }
    finally { setBusy(false); }
  };

  const finishSetup = async () => {
    if (!saved) return;
    await queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
    window.location.assign(getReturnTo());
  };

  // ── Recovery codes saved screen ─────────────────────────────────────────
  if (recoveryCodes) return (
    <main className="min-h-screen grid place-items-center bg-muted/30 p-4">
      <section className="w-full max-w-lg rounded-xl border bg-card p-6 shadow-sm space-y-5">
        <ShieldCheck className="h-10 w-10 text-primary" />
        <h1 className="text-2xl font-semibold">Save your recovery codes</h1>
        <p className="text-sm text-muted-foreground">These codes are shown only once. Store them in a secure password manager; each can be used once.</p>
        <pre className="grid grid-cols-2 gap-2 rounded-md bg-muted p-4 text-sm select-all">
          {recoveryCodes.map(value => <code key={value}>{value}</code>)}
        </pre>
        <label className="flex gap-2 text-sm items-start">
          <input type="checkbox" checked={saved} onChange={e => setSaved(e.target.checked)} />
          I have saved these recovery codes securely.
        </label>
        <Button className="w-full" disabled={!saved} onClick={finishSetup}>Continue securely</Button>
      </section>
    </main>
  );

  // ── Email OTP screen ────────────────────────────────────────────────────
  if (status?.mode === "email-otp") return (
    <main className="min-h-screen grid place-items-center bg-muted/30 p-4">
      <section className="w-full max-w-md rounded-xl border bg-card p-6 shadow-sm space-y-5">
        <div className="flex items-center gap-3">
          <div className="rounded-full bg-primary/10 p-3">
            <Mail className="h-7 w-7 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold">Check your email</h1>
            <p className="text-sm text-muted-foreground">We emailed you a 6-digit sign-in code</p>
          </div>
        </div>

        {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}
        {resent && <Alert><AlertDescription>A new code has been sent to your email.</AlertDescription></Alert>}

        {trustSaveFailed ? (
          <div className="space-y-4">
            <Alert variant="destructive" role="alert">
              <AlertDescription>
                Your sign-in is verified, but we could not save this device as trusted.
                You may need another email code the next time you sign in.
              </AlertDescription>
            </Alert>
            <Button className="w-full" onClick={() => window.location.assign(getReturnTo())}>
              Continue to your account
            </Button>
          </div>
        ) : (
          <form onSubmit={submitEmailOtp} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email-otp-code">6-digit code</Label>
              <Input
                id="email-otp-code"
                autoFocus
                value={code}
                onChange={e => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="123456"
              />
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
              <input
                type="checkbox"
                checked={trustDevice}
                onChange={e => setTrustDevice(e.target.checked)}
                className="rounded"
              />
              Trust this device for 15 days
            </label>
            <Button className="w-full" disabled={busy || code.length !== 6}>
              {busy ? "Verifying…" : "Verify"}
            </Button>
          </form>
        )}

        {!trustSaveFailed && <div className="text-center">
          <button
            type="button"
            className="text-sm text-primary hover:underline disabled:opacity-50"
            onClick={resendEmailOtp}
            disabled={busy}
          >
            Resend code
          </button>
        </div>}
      </section>
    </main>
  );

  // ── Legacy TOTP setup / challenge screen ────────────────────────────────
  const isSetup = status?.mode === "setup";
  return (
    <main className="min-h-screen grid place-items-center bg-muted/30 p-4">
      <section className="w-full max-w-md rounded-xl border bg-card p-6 shadow-sm space-y-5">
        <div className="flex items-center gap-3">
          <div className="rounded-full bg-primary/10 p-3">
            <ShieldCheck className="h-7 w-7 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold">Secure your account</h1>
            <p className="text-sm text-muted-foreground">
              {isSetup ? "Set up your authenticator app" : "Verify your sign-in"}
            </p>
          </div>
        </div>

        {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}

        {isSetup && (
          busy && !setup
            ? <p className="text-sm text-muted-foreground">Preparing your secure key…</p>
            : setup && (
              <div className="space-y-3">
                <p className="text-sm">Scan this code with an authenticator app, or enter the manual key below.</p>
                <div className="flex justify-center rounded-lg bg-white p-3">
                  <QRCodeSVG value={setup.otpauthUri} size={180} />
                </div>
                <Label htmlFor="two-factor-secret">Manual setup key</Label>
                <div className="flex gap-2">
                  <Input id="two-factor-secret" readOnly value={setup.secret} />
                  <Button variant="outline" size="icon" onClick={() => navigator.clipboard.writeText(setup.secret)} aria-label="Copy setup key">
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )
        )}

        <form onSubmit={submitTotp} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="two-factor-code">
              {recovery ? "Recovery code" : "6-digit authenticator code"}
            </Label>
            <Input
              id="two-factor-code"
              autoFocus
              value={code}
              onChange={e => setCode(recovery ? e.target.value : e.target.value.replace(/\D/g, "").slice(0, 6))}
              inputMode={recovery ? "text" : "numeric"}
              autoComplete="one-time-code"
              placeholder={recovery ? "XXXX-XXXX" : "123456"}
            />
          </div>
          <Button className="w-full" disabled={busy || (isSetup && !setup)}>
            {busy ? "Verifying…" : isSetup ? "Confirm and enable" : "Verify"}
          </Button>
        </form>

        {!isSetup && (
          <button
            className="text-sm text-primary hover:underline"
            onClick={() => { setRecovery(!recovery); setCode(""); setError(""); }}
          >
            {recovery ? "Use an authenticator code instead" : "Use a recovery code"}
          </button>
        )}
      </section>
    </main>
  );
}
